import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useErrors } from '../../../../hooks/errors/use-errors';
import { useNative } from '../../../../hooks/native/use-native';
import { useHttpClient } from '../../../../utils/clients/rest-client';
import { toastSuccess } from '../../../../utils/toast';
import { useUpdateChannel } from '../../hooks/channel/use-update-channel';
import {
  BlindCalibrateData,
  BlindCalibrateManuallyData,
  BlindCalibrateManuallyError,
  BlindSetDirectionData,
  BlindSetPositionData,
  BlindSetSensitivityData,
  BlindSetSensitivityError,
  BlindSwapOutData,
  CalibrateRequest,
  ManuallyCalibrateRequest,
  SetDirectionRequest,
  SetPositionRequest,
  SetSensitivityRequest,
  SwapOutRequest,
} from '../../types/openapi/openapi-types';

export const useBlindSetPosition = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<BlindSetPositionData>, AxiosError<void>, SetPositionRequest>(
    ['blind-setPosition'],
    async (body) => {
      fireVibration();
      return await httpClient.blind.blindSetPosition(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({ status: data.publishStatus, deviceId: body.deviceId });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useBlindSetDirection = () => {
  const httpClient = useHttpClient();
  const { resetCalibrateState } = useUpdateChannel();
  const { handleRestErrors, handleLavvaResolve } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<BlindSetDirectionData>, AxiosError<void>, SetDirectionRequest>(
    ['blind-setDirection'],
    async (body) => {
      fireVibration();
      return await httpClient.blind.blindSetDirection(body);
    },
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({
          status: data.publishStatus,
          deviceId: body.deviceId,
          onSuccess: () => {
            resetCalibrateState(body.channelId, data);
          },
        });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useBlindCalibrate = () => {
  const { t: td } = useTranslation('channel-settings');
  const httpClient = useHttpClient();
  const { handleRestErrors, handleLavvaResolve } = useErrors();

  return useMutation<AxiosResponse<BlindCalibrateData>, AxiosError<void>, CalibrateRequest>(
    ['blind-calibrate'],
    async (body) => await httpClient.blind.blindCalibrate(body),
    {
      onSuccess: ({ data }, body) => {
        handleLavvaResolve({
          status: data.publishStatus,
          deviceId: body.deviceId,
          onSuccess: () => {
            toastSuccess({ content: td('toast.calibrationStartSent') });
          },
        });
      },
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useBlindSwapOut = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<AxiosResponse<BlindSwapOutData>, AxiosError<void>, SwapOutRequest>(
    ['device-swapIn'],
    async (body) => {
      fireVibration();
      return await httpClient.blind.blindSwapOut(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useBlindCalibrateManually = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<
    AxiosResponse<BlindCalibrateManuallyData>,
    AxiosError<BlindCalibrateManuallyError>,
    ManuallyCalibrateRequest
  >(
    ['device-calibarteManually'],
    async (body) => {
      fireVibration();
      return await httpClient.blind.blindCalibrateManually(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useSetBlindSensitivityAOK = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<BlindSetSensitivityData>,
    AxiosError<BlindSetSensitivityError>,
    SetSensitivityRequest
  >(['blind-setBlindSensitivityAOK'], async (body) => await httpClient.blind.blindSetSensitivity(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};
